import { stringToFormFieldComponent, stringToFormFieldParser } from "components/Form";
import { Form } from "components/Form/components/Form/Form";
import Tooltip from "components/Tooltip/Tooltip";
import { FeRecordType, FeSubrecordType, FormField, RecordId } from "config/config";
import { Fragment } from "react";
import { Field } from "react-final-form";
import "./ResourceEditFields.scss";

interface ResourceEditFieldsProps {
    formStructure?: FormField[];
    isDisabled: boolean;
    isReadOnly: boolean;
    writeOnlyFieldsWithData: any;
    resourceTypeName: FeRecordType | FeSubrecordType;
    recordId: RecordId;
    fetchResourceData?: (() => Promise<any>) | null;
    setNestedFormSaveData?: (value: any) => void;
    nestedFormSaveData?: { [key: string]: () => void };
    defaultValues?: any;
    currentValues?: any;
    additionalFieldProps?: { [key: string]: any };
    enableSaveManually?: (value: boolean) => void;
    isNewRecord?: boolean;
    isLoaded?: boolean;
}

export const ResourceEditFields = ({
    formStructure = [],
    isDisabled,
    isReadOnly,
    writeOnlyFieldsWithData,
    resourceTypeName,
    recordId,
    fetchResourceData = null,
    setNestedFormSaveData,
    nestedFormSaveData,
    defaultValues,
    currentValues,
    additionalFieldProps = {},
    enableSaveManually,
    isNewRecord,
    isLoaded,
}: ResourceEditFieldsProps) => (
    <>
        {formStructure.map((field) => {
            const dependee = field.fieldAttr?.dependee;
            const isFieldRendered =
                !dependee ||
                (dependee.fieldValue !== undefined &&
                    currentValues?.[dependee.fieldName] === dependee.fieldValue) ||
                (dependee.isValueSet === true && currentValues?.[dependee.fieldName]) ||
                (dependee.isValueSet === false && !currentValues?.[dependee.fieldName]);

            const isFieldDisabled = field.fieldAttr?.getDisabled?.(currentValues);

            const { conditionalFieldProps } = field;
            const isConditionMet =
                conditionalFieldProps &&
                currentValues[conditionalFieldProps.condition.fieldName] ===
                    conditionalFieldProps.condition.fieldValue;
            let conditionalReadOnly;
            if (isConditionMet) {
                conditionalReadOnly = conditionalFieldProps.fieldProps.isReadOnly;
                currentValues[field.name] = conditionalFieldProps.fieldProps.value;
            }
            const note = field.fieldProps?.fieldNote?.(currentValues);
            const defaultValue =
                field.name in writeOnlyFieldsWithData
                    ? undefined
                    : defaultValues?.[field.name] ??
                      (field.fieldProps?.defaultValue?.value !== undefined
                          ? field.fieldProps?.defaultValue?.value
                          : field.fieldProps?.defaultValue);

            const fieldComponent = (
                <Fragment key={`resource-edit-form-${field.component + field.name}`}>
                    <Form.Row
                        className={`form__row--${field.component}`}
                        key={`resource-edit-form-${field.component + field.name}`}
                    >
                        <Field
                            name={field.name}
                            fieldProps={{
                                ...field.fieldProps,
                                hasValue: writeOnlyFieldsWithData[field.name],
                                resourceTypeName: resourceTypeName,
                                recordId: recordId,
                                enableSaveManually: field.fieldProps.canEnableSaveManually
                                    ? enableSaveManually
                                    : undefined,
                                isConditionMet: field.fieldProps.isConditionMet?.(currentValues),
                                fieldReplacement: field.fieldProps.fieldReplacement,
                                isNewRecord,
                                initialAutocompleteOption:
                                    field.fieldProps.getInitialAutocompleteOption?.(currentValues),
                                isLoaded,
                                defaultValue,
                                valueTransformer: field.fieldProps.valueTransformer
                                    ? (value: unknown) =>
                                          field.fieldProps.valueTransformer(value, currentValues)
                                    : undefined,
                                ...additionalFieldProps,
                            }}
                            fieldAttr={{
                                ...field.fieldAttr,
                                disabled: isDisabled
                                    ? true
                                    : field.fieldProps?.isReadOnly ||
                                      conditionalReadOnly ||
                                      (recordId && field.fieldAttr?.isChangeable === false) ||
                                      isReadOnly ||
                                      isFieldDisabled,
                                hidden:
                                    field.fieldAttr?.hidden ||
                                    (field.fieldProps?.checkAccess &&
                                        additionalFieldProps?.isSSPHidden),
                                fieldTooltip: field.fieldAttr?.fieldTooltip?.(currentValues),
                            }}
                            component={stringToFormFieldComponent[field.component]}
                            validate={field.validation}
                            parse={
                                stringToFormFieldParser[field.component]
                                    ? stringToFormFieldParser[field.component]
                                    : (value) => value
                            }
                            isEqual={(a, b) => {
                                if (field.isEqual) {
                                    return field.isEqual(a, b);
                                }
                                return a === b;
                            }}
                            fetchResourceData={fetchResourceData}
                            setNestedFormSaveData={setNestedFormSaveData}
                            nestedFormSaveData={nestedFormSaveData}
                            defaultValue={defaultValue}
                        />
                    </Form.Row>
                    {note && !additionalFieldProps?.renderIconOnly ? (
                        <Form.Row key={`resource-edit-form-${field.component + field.name}-note`}>
                            {note}
                        </Form.Row>
                    ) : null}
                </Fragment>
            );

            return isFieldRendered ? (
                additionalFieldProps?.renderIconOnly && field.fieldProps?.iconTooltipOverlay ? (
                    <Tooltip
                        overlay={field.fieldProps.iconTooltipOverlay}
                        key={`resource-edit-form-${field.component + field.name}`}
                    >
                        {fieldComponent}
                    </Tooltip>
                ) : (
                    fieldComponent
                )
            ) : null;
        })}
    </>
);
